import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { explainCopy } from '../../lib/explainCopy'
import { vizDiv } from '../shared/viz'
import type { TornadoRow } from '../../types/electionSim'

export interface TornadoCardProps {
  tornado: TornadoRow[]
  onCreateTurf: (geoUnitIds: string[]) => void
  onExport: (geoUnitIds: string[]) => void
}

/**
 * Tornado — movers & drags (spec §slice-4): signed, both-ended marginal net Dem votes per 100
 * shifts, drawn as diverging bars off a center line — movers right (viz.div.pos, green value
 * label), drags left (viz.div.neg). CTAs
 * turn the POSITIVE-marginal precincts into a turf or export the target cohort — never the
 * negative drags. Header tooltip is wired to `explainCopy.tornado`.
 */
export function TornadoCard({ tornado, onCreateTurf, onExport }: TornadoCardProps) {
  const rows = (tornado ?? []).slice().sort(
    (a, b) => b.marginalNetDemVotesPer100Shifts - a.marginalNetDemVotesPer100Shifts,
  )
  const positives = rows.filter((r) => r.marginalNetDemVotesPer100Shifts > 0)
  const positiveIds = positives.map((r) => r.geoUnitId)
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.marginalNetDemVotesPer100Shifts)), 0)
  const fmt1 = (v: number) => (v >= 0 ? `+${v.toFixed(1)}` : `−${Math.abs(v).toFixed(1)}`)

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
        Tornado — movers &amp; drags
      </Text>
      <HStack gap={1} mb={3}>
        <Text fontWeight="semibold" fontSize="sm">Net votes per +100 shifts</Text>
        <Tooltip showArrow content={explainCopy.tornado()} positioning={{
          placement: "top"
        }}>
          <Text as="span" fontSize="xs" color="info.fg" cursor="help" borderWidth="1px" borderColor="info.fg" borderRadius="full" px={1.5} aria-label="What is this?">i</Text>
        </Tooltip>
      </HStack>

      {rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No sensitivity data for this run.</Text>
      ) : (
        rows.map((r) => {
          const v = r.marginalNetDemVotesPer100Shifts
          const pct = max > 0 ? (Math.abs(v) / max) * 50 : 0 // half-width; center at 50%
          const positive = v >= 0
          return (
            <Flex key={r.geoUnitId} align="center" gap={2} mb={2}>
              <Text fontSize="xs" w="120px" flexShrink={0} lineClamp={1} title={r.unitName}>{r.unitName}</Text>
              <Box position="relative" flex="1" h="14px" bg="gray.50" borderRadius="sm">
                <Box position="absolute" left="50%" top={0} bottom={0} w="1px" bg="gray.300" />
                <Box position="absolute" top="2px" bottom="2px"
                  left={positive ? '50%' : `${50 - pct}%`} w={`${pct}%`}
                  bg={positive ? vizDiv.pos : vizDiv.neg} borderRadius="sm" />
              </Box>
              <Text fontSize="xs" fontWeight="bold" w="48px" textAlign="right" flexShrink={0}
                color={positive ? 'green.600' : 'red.500'}>{fmt1(v)}</Text>
            </Flex>
          );
        })
      )}

      {rows.length > 0 && positives.length === 0 && (
        <Text fontSize="xs" color="gray.600" mt={2}>{explainCopy.noPositivePrecinct()}</Text>
      )}

      <HStack mt={4} gap={3} wrap="wrap">
        <Button size="sm" colorPalette="brand" disabled={positiveIds.length === 0} onClick={() => onCreateTurf(positiveIds)}>
          Create turf from top movers
        </Button>
        <Button size="sm" variant="outline" disabled={positiveIds.length === 0} onClick={() => onExport(positiveIds)}>
          Export target cohort
        </Button>
      </HStack>
    </Box>
  );
}
